import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useSEO from '../../useSEO';
import { useSite } from '../../components/StoreLayout';
import { STORE_BASE } from '../../routeBase';
import { LENS_INDICES, calcMbsAndThickness, compareAcrossIndices } from './lensThicknessMath';

const lbl = { fontSize: 12.5, fontWeight: 600, color: 'var(--sf-primary)', display: 'block', marginBottom: 5 };
const inp = { width: '100%', padding: '9px 11px', border: '1.5px solid #e5e5e5', borderRadius: 8, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit' };
const field = (label, value, onChange, opts = {}) => (
  <div style={{ marginBottom: 14 }}>
    <label style={lbl}>{label}</label>
    <input
      type="number" step={opts.step || '0.25'} value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={opts.placeholder}
      style={inp}
    />
    {opts.hint && <div style={{ fontSize: 11, color: 'var(--sf-muted)', marginTop: 4 }}>{opts.hint}</div>}
  </div>
);

export default function LensThicknessCalculator() {
  const { content: c } = useSite();
  useSEO({
    title: c['tools.lens-thickness.meta_title'] || 'Lens Thickness & MBS Calculator for Opticians',
    description: c['tools.lens-thickness.meta_description'] || 'Free lens thickness and Minimum Blank Size (MBS) calculator for dispensing opticians. Enter frame size, PD and prescription to estimate edge/center thickness across lens indexes 1.50–1.74.',
    keywords: c['tools.lens-thickness.meta_keywords'] || 'lens thickness calculator, MBS calculator, minimum blank size, edge thickness, high index lens calculator, optician tools',
    canonical: `${window.location.origin}/store/tools/lens-thickness-calculator`,
  });

  const [frameA, setFrameA] = useState('52');
  const [frameB, setFrameB] = useState('40');
  const [bridge, setBridge] = useState('18');
  const [pd, setPd] = useState('64');
  const [sph, setSph] = useState('-6.00');
  const [cyl, setCyl] = useState('0');
  const [index, setIndex] = useState('1.50');
  const [minThickness, setMinThickness] = useState('1.0');
  const [edging, setEdging] = useState('2');

  const result = useMemo(() => calcMbsAndThickness({
    frameA, frameB, bridge, patientPd: pd, sph, cyl, index, edgingAllowance: edging, minThickness,
  }), [frameA, frameB, bridge, pd, sph, cyl, index, edging, minThickness]);

  const comparison = useMemo(() => compareAcrossIndices({ frameA, frameB, sph, cyl, minThickness }),
    [frameA, frameB, sph, cyl, minThickness]);

  const card = { background: '#fff', border: '1px solid #eee', borderRadius: 14, padding: 24 };

  return (
    <div style={{ background: 'var(--sf-body-bg)' }}>
      {/* HERO */}
      <section style={{ maxWidth: 900, margin: '0 auto', padding: '48px 24px 16px', textAlign: 'center' }}>
        <Link to={`${STORE_BASE}/tools`} style={{ fontSize: 12.5, color: 'var(--sf-hover)', fontWeight: 600, textDecoration: 'none' }}>
          <i className="ti ti-arrow-left" style={{ fontSize: 13, verticalAlign: -1 }} /> All free tools
        </Link>
        <h1 style={{ fontSize: 'clamp(24px,3.6vw,34px)', fontWeight: 800, color: 'var(--sf-primary)', margin: '14px 0 12px' }}>
          Lens Thickness &amp; MBS Calculator
        </h1>
        <p style={{ fontSize: 14.5, color: 'var(--sf-muted)', maxWidth: 620, margin: '0 auto', lineHeight: 1.7 }}>
          Estimate the Minimum Blank Size and edge/center thickness for a prescription before you cut —
          and see how much thinner it gets in a higher index.
        </p>
      </section>

      {/* CALCULATOR */}
      <section style={{ maxWidth: 1100, margin: '24px auto 0', padding: '0 24px' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(280px,1fr) minmax(300px,1.1fr)', gap: 24 }} className="ltc-grid">
          {/* INPUTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Frame &amp; Prescription</h3>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {field('Frame Eye Size — A (mm)', frameA, setFrameA)}
              {field('Frame Height — B (mm)', frameB, setFrameB)}
              {field('Bridge / DBL (mm)', bridge, setBridge)}
              {field('Patient PD (mm)', pd, setPd, { hint: 'Binocular PD, split evenly per eye' })}
              {field('Sphere (D)', sph, setSph, { placeholder: 'e.g. -6.00' })}
              {field('Cylinder (D)', cyl, setCyl, { placeholder: 'e.g. -1.50 or +1.50', hint: 'Either notation works — enter it as printed on the Rx' })}
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Lens Index</label>
              <select value={index} onChange={e => setIndex(e.target.value)} style={inp}>
                {LENS_INDICES.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              {field('Min. Center/Edge Thickness (mm)', minThickness, setMinThickness, { hint: 'Your lab\'s minimum cut — 1.0mm is typical' })}
              {field('Edging Allowance (mm)', edging, setEdging, { hint: 'Safety margin added to MBS' })}
            </div>
          </div>

          {/* RESULTS */}
          <div style={card}>
            <h3 style={{ fontSize: 15, fontWeight: 700, color: 'var(--sf-primary)', margin: '0 0 16px' }}>Result</h3>
            {result.error && (
              <div style={{ background: '#fef2f2', color: '#b91c1c', fontSize: 13, padding: '10px 12px', borderRadius: 8, marginBottom: 14 }}>
                {result.error}
              </div>
            )}
            {result.mbs != null && (
              <>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 18 }}>
                  <Stat label="Minimum Blank Size" value={`${result.mbs.toFixed(1)} mm`} highlight />
                  <Stat label="Effective Diameter" value={`${result.ed.toFixed(1)} mm`} />
                  <Stat label="Decentration / eye" value={`${result.decentration.toFixed(1)} mm`} />
                  <Stat label="Spherical Equivalent" value={`${result.se >= 0 ? '+' : ''}${result.se.toFixed(2)} D`} />
                </div>
                {result.thickness && (
                  <div style={{ background: 'color-mix(in srgb, var(--sf-accent) 14%, #fff)', borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
                    <div style={{ fontSize: 11.5, color: 'var(--sf-muted)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700 }}>
                      {result.thickness.label}
                    </div>
                    <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--sf-primary)', marginTop: 2 }}>
                      {result.thickness.value.toFixed(1)} mm
                    </div>
                  </div>
                )}
              </>
            )}

            {comparison.length > 0 && (
              <>
                <h4 style={{ fontSize: 12.5, fontWeight: 700, color: 'var(--sf-primary)', textTransform: 'uppercase', letterSpacing: 0.4, margin: '18px 0 10px' }}>
                  Same prescription, across indexes
                </h4>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {comparison.map(row => (
                        <tr key={row.index} style={{ borderTop: '1px solid #f0f0f0' }}>
                          <td style={{ padding: '8px 4px', color: 'var(--sf-muted)' }}>{row.label}</td>
                          <td style={{ padding: '8px 4px', textAlign: 'right', fontWeight: 700, color: String(row.index) === index ? 'var(--sf-hover)' : 'var(--sf-primary)' }}>
                            {row.thickness == null ? '—' : `${row.thickness.toFixed(1)} mm`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            <p style={{ fontSize: 11, color: 'var(--sf-muted)', lineHeight: 1.6, marginTop: 18 }}>
              Estimate for planning purposes, based on standard boxing-system geometry (effective diameter =
              frame diagonal) and single-surface sag calculation. Always confirm exact thickness and MBS with
              your lab's edging software before cutting.
            </p>
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section style={{ background: 'var(--sf-primary)', color: '#fff', marginTop: 48 }}>
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px', display: 'flex', flexWrap: 'wrap', gap: 20, alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, margin: '0 0 6px' }}>Stock up your shop for less</h3>
            <p style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.75)', margin: 0, maxWidth: 460 }}>
              View our latest wholesale frame collections — acetate, TR90 and metal, shipped worldwide.
            </p>
          </div>
          <Link to={`${STORE_BASE}/shop`} className="sf-btn-outline" style={{
            background: '#fff', color: 'var(--sf-primary)', padding: '11px 22px', borderRadius: 8,
            fontWeight: 700, fontSize: 13, textDecoration: 'none', flexShrink: 0,
          }}>
            Browse Wholesale Catalog
          </Link>
        </div>
      </section>

      <style>{`
        @media (max-width: 780px) {
          .ltc-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div style={{
      background: highlight ? 'var(--sf-primary)' : '#f7f8fa', color: highlight ? '#fff' : 'var(--sf-primary)',
      borderRadius: 10, padding: '12px 14px',
    }}>
      <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 700, opacity: highlight ? 0.8 : 0.6 }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 3 }}>{value}</div>
    </div>
  );
}
